/**
 * The notice half of the withheld-read contract for the monitoring pages.
 *
 * A failed first read states that nothing was read and offers a retry; a
 * rejected background refetch over a successful read keeps the figures and
 * says when they were read. Four tiles reading '--' with nothing said would
 * leave the operator no way to tell an outage from an empty book.
 */

import { Alert, Button } from '@aaimplatform/ui';
import { readTime } from './read-state';

interface ReadNoticeProps {
  /** What the page reads, in a sentence ("Asset classification data"). */
  subject: string;
  /** The first read rejected and produced nothing. */
  failed: boolean;
  /** A background refetch rejected after a successful read. */
  refreshFailed: boolean;
  /** When the figures on screen were read (TanStack `dataUpdatedAt`). */
  dataUpdatedAt: number;
  /** The rejection, when the read produced one. */
  error: unknown;
  onRetry: () => void;
}

export function ReadNotice({
  subject,
  failed,
  refreshFailed,
  dataUpdatedAt,
  error,
  onRetry,
}: ReadNoticeProps) {
  if (!failed && !refreshFailed) return null;
  const detail = error instanceof Error ? error.message : undefined;

  const retry = (
    <Button variant="tertiary" size="small" onClick={onRetry}>
      Retry
    </Button>
  );

  if (failed) {
    return (
      <div className="mb-6" role="alert">
        <Alert type="error" title={`${subject} could not be read`}>
          <div className="space-y-2">
            {detail && <div className="text-xs">{detail}</div>}
            <div className="text-xs">
              The figures below are withheld, not zero. Nothing here states what
              your institution holds; the read itself failed.
            </div>
            {retry}
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mb-6" role="status">
      <Alert type="warning" title={`${subject} refresh failed`}>
        <div className="space-y-2">
          <div className="text-xs">
            {`The figures shown are from the read at ${readTime(dataUpdatedAt)}.${detail ? ` The latest refresh failed: ${detail}` : ''}`}
          </div>
          {retry}
        </div>
      </Alert>
    </div>
  );
}
